import React, { useState, useEffect, lazy, Suspense } from "react";
import { Divider } from "primereact/divider";
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { ConfirmDialog } from "primereact/confirmdialog";
import "./footer.css";
import { useAuthStore } from "~/store/authStore";
import useRatingStore from "~/store/ratingStore";
import WorkspaceMenus from "./WorkspaceMenus";
import RouterRegistrationDialog from "../RouterRegistrationDialog/RouterRegistrationDialog";
import RemoteLogIndicator from "./RemoteLogIndicator";
import { useToast } from "../ToastProvider";
import { useConnectivityCheck } from "../../hooks/useConnectivityCheck";
// Lazy load heavy modal
const RouterLoginModal = lazy(() => import("../Modals/RouterLoginModal"));
const AdminNewsMessageModal = lazy(
  () => import("../Modals/AdminNewsMessageModal/AdminNewsMessageModal"),
);
const ServerConnectionsModal = lazy(
  () => import("../Modals/ServerConnectionsModal"),
);
import { router } from "../../data/Services/ordersRouter";
import marketdata from "../../data/Services/marketdata";
import { isMobilePhone } from "../../utils/mobileDetection";
import useNarrowViewport from "../../hooks/useNarrowViewport";

function Footer() {
  const isNarrowViewport = useNarrowViewport(768);
  const [showHelpPanel, setShowHelpPanel] = useState(false);
  const { getUsername, getBroker } = useAuthStore();
  const { showInfo, showSuccess, showWarning } = useToast();
  const showRatingModal = useRatingStore((state) => state.showModal);
  const username = getUsername();

  // Enhanced connection state detection (same as OfflineOverlay)
  const [online, setOnline] = useState(false);
  const { browserOnline, connectivityCheckFailed } = useConnectivityCheck();
  const [isInitializing, setIsInitializing] = useState(true);

  // Router connection status
  const [routerOnline, setRouterOnline] = useState(false);
  const [routerBroker, setRouterBroker] = useState("");

  // Confirmation dialog state
  const [showDisconnectConfirm, setShowDisconnectConfirm] = useState(false);

  // Determine comprehensive online status
  const determineOnlineStatus = () => {
    const isConnected = marketdata.state === "online";
    const isDisconnected = marketdata.state === "offline";

    // Check SignalR connection state if available
    const signalRConnection = marketdata.signalRConnection;
    let signalRState = null;
    let isSignalRDisconnected = false;
    let isSignalRConnecting = false;

    if (signalRConnection) {
      signalRState =
        signalRConnection.state ||
        signalRConnection.connectionState ||
        signalRConnection.connection?.state;
      isSignalRDisconnected =
        signalRState === "Disconnected" ||
        signalRState === 6 ||
        signalRState === "disconnected" ||
        !signalRConnection.connectionId;
      isSignalRConnecting =
        signalRState === "Connecting" ||
        signalRState === "Reconnecting" ||
        signalRState === 0 ||
        signalRState === 2 ||
        signalRState === "connecting" ||
        signalRState === "reconnecting";
    }

    // Determine if fully connected
    const isFullyConnected =
      isConnected ||
      signalRState === "Connected" ||
      signalRState === 1 ||
      signalRState === "connected";

    // During initialization (first 5 seconds), don't mark as offline unless explicitly disconnected
    if (isInitializing) {
      // Only mark as offline if explicitly disconnected or browser is offline
      return !isDisconnected && browserOnline;
    }

    // Comprehensive offline check (after initialization)
    // Consider "connecting" states as online to avoid red flash during reconnection
    const isCurrentlyOffline =
      isDisconnected ||
      (isSignalRDisconnected && !isSignalRConnecting) ||
      (!isSignalRConnecting && !isConnected && !isFullyConnected) ||
      !browserOnline ||
      connectivityCheckFailed;

    return !isCurrentlyOffline;
  };

  // Determine router online status
  const determineRouterOnlineStatus = () => {
    if (!router) return false;

    // Check if router is connected and operational
    const isRouterConnected = router.state === "online";
    const isRouterNotIdle = !router.idle;
    const hasValidConnection = router.isConnectionOK && router.isConnectionOK();

    return isRouterConnected && isRouterNotIdle && hasValidConnection;
  };

  // Update router status
  const updateRouterStatus = () => {
    setRouterOnline(determineRouterOnlineStatus());
    setRouterBroker(router?.broker || "");
  };

  useEffect(() => {
    // Marketdata receiver for connection events
    const receiver = {
      OnConnected: () => {
        console.log("🔗 Footer: Marketdata connected");
        setOnline(determineOnlineStatus());
      },
      OnDisconnected: () => {
        console.log("🔗 Footer: Marketdata disconnected");
        setOnline(determineOnlineStatus());
      },
    };

    if (marketdata && marketdata.registerReceiver) {
      marketdata.registerReceiver(receiver);
    }

    // Initial status check
    setOnline(determineOnlineStatus());

    // End initialization period after 5 seconds
    const initializationTimer = setTimeout(() => {
      setIsInitializing(false);
      console.log(
        "🔗 Footer: Initialization period ended, full connection checks active",
      );
    }, 5000);

    // Periodic status update (every 1s)
    const statusInterval = setInterval(() => {
      setOnline(determineOnlineStatus());
    }, 1000);

    // Router receiver for connection events
    const routerReceiver = {
      OnConnected: () => {
        console.log("🔗 Footer: Router connected");
        updateRouterStatus();
      },
      OnDisconnected: () => {
        console.log("🔗 Footer: Router disconnected");
        updateRouterStatus();
      },
    };

    if (router && router.registerReceiver) {
      router.registerReceiver(routerReceiver);
    }

    // Initial router status check
    updateRouterStatus();

    // Periodic router status update (every 2s)
    const routerStatusInterval = setInterval(() => {
      updateRouterStatus();
    }, 2000);

    // Listen for global requests to open router login (from other components)
    const handleOpenRouterLogin = () => {
      console.log(
        "🔔 Footer: openRouterLogin event received - showing login modal",
      );
      setIsRouterLoginModalVisible(true);
    };
    window.addEventListener("openRouterLogin", handleOpenRouterLogin);

    return () => {
      clearInterval(statusInterval);
      clearInterval(routerStatusInterval);
      clearTimeout(initializationTimer);
      if (marketdata && marketdata.unregisterReceiver) {
        marketdata.unregisterReceiver(receiver);
      }
      if (router && router.unregisterReceiver) {
        router.unregisterReceiver(routerReceiver);
      }
      window.removeEventListener("openRouterLogin", handleOpenRouterLogin);
    };
  }, [browserOnline, connectivityCheckFailed]);

  const [isRouterLoginModalVisible, setIsRouterLoginModalVisible] =
    useState(false);
  const [isAdminNewsModalVisible, setIsAdminNewsModalVisible] = useState(false);
  const [showRouterRegistration, setShowRouterRegistration] = useState(false);
  const [showServerConnections, setShowServerConnections] = useState(false);

  // Check if user is admin (@itevolution.com.br)
  const isAdmin = username && username.endsWith("@itevolution.com.br");

  // Handle router button click
  const handleRouterButtonClick = async () => {
    // Block router login on mobile phones
    if (isMobilePhone()) {
      showInfo(
        "Negociação não disponível neste dispositivo. Use um tablet, computador ou abra a tela do seu celular dobrável.",
        "Negociação Indisponível",
      );
      return;
    }

    if (routerOnline) {
      // Router is online, ask for confirmation to disconnect
      setShowDisconnectConfirm(true);
    } else {
      // Router is offline, check if user has router access
      const broker = getBroker();

      if (!broker) {
        showInfo("Faça login no MarketData primeiro.", "Login Necessário");
        return;
      }

      if (!username) {
        showInfo("Faça login no MarketData primeiro.", "Login Necessário");
        return;
      }

      // Check if marketdata is online before checking router access
      if (!marketdata || marketdata.state !== "online") {
        showInfo(
          "Conecte-se ao MarketData primeiro para verificar acesso ao roteador.",
          "MarketData Offline",
        );
        return;
      }

      // Simulator-only logins (non-numeric, e.g. email) can only trade in
      // simulator, which needs no router enablement (the backend bypasses the
      // isEnabled flag for SIMULATOR). The login modal forces demo mode for
      // them anyway, so skip the real-trading gate and open it directly —
      // otherwise they get stuck on "solicite acesso" / registration (#338).
      const isSimulatorOnly = username && !/^\d+$/.test(username);
      if (isSimulatorOnly) {
        setIsRouterLoginModalVisible(true);
        return;
      }

      // Check if router is enabled for this user
      try {
        const isEnabled = await router.isRouterEnabled(username);

        if (isEnabled) {
          // User has router access, show login modal
          setIsRouterLoginModalVisible(true);
        } else {
          // User doesn't have router access
          if (broker === "XPI") {
            // XPI: Show registration dialog
            console.log(
              "🔔 Router not enabled for XPI user, showing registration dialog",
            );
            setShowRouterRegistration(true);
          } else {
            // Other brokers: Show info message
            const brokerNames = {
              TZ: "Terra Investimentos",
              CLEAR: "Clear (XP)",
              RICO: "Rico Investimentos",
            };
            const brokerName = brokerNames[broker] || broker;

            showInfo(
              `Para acessar o roteador da ${brokerName}, visite o site da corretora e solicite acesso.`,
              "Acesso ao Roteador",
            );
          }
        }
      } catch (error) {
        console.error("Error checking router access:", error);
        // Show user-friendly error message (service unavailable, not "user has no access")
        const errorMessage =
          error.message ||
          "Não foi possível conectar ao serviço de roteamento. Tente novamente mais tarde.";
        showWarning(errorMessage, "Serviço Indisponível");
      }
    }
  };

  // Handle router disconnect confirmation
  const handleDisconnectConfirm = () => {
    if (router && router.stop) {
      console.log("🔌 Disconnecting router...");
      router.stop();
      setShowDisconnectConfirm(false);
    }
  };

  // Handle router disconnect rejection
  const handleDisconnectReject = () => {
    setShowDisconnectConfirm(false);
  };

  // Handle router registration submission
  const handleRouterRegistrationSubmit = async (formData) => {
    const broker = getBroker();

    // Check if marketdata is online before submitting
    if (!marketdata || marketdata.state !== "online") {
      throw new Error(
        "Conecte-se ao MarketData primeiro para enviar a solicitação.",
      );
    }

    try {
      const request = {
        user: username,
        username: formData.fullName,
        cpf: formData.cpf,
        email: formData.email,
        tel: formData.phone,
      };

      const success = await router.grantRouterAccess(broker, request);

      if (success) {
        showSuccess(
          "Solicitação enviada com sucesso! Você receberá um email com instruções para completar o acesso ao roteador.",
          "Solicitação Enviada",
        );
        setShowRouterRegistration(false);
      } else {
        throw new Error(
          "Falha ao enviar solicitação. Verifique os dados e tente novamente.",
        );
      }
    } catch (error) {
      console.error("Router registration error:", error);
      throw error;
    }
  };

  // Styling is handled in CSS and responds to body.dark-mode-custom

  const toggleHelpPanel = () => {
    setShowHelpPanel((prev) => !prev);
  };
  return (
    <footer
      className="app-footer"
      style={{
        padding: "5px",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        flexWrap: "nowrap",
      }}
    >
      <div
        className="footer-info"
        style={{
          order: 1,
          whiteSpace: "nowrap",
          display: "flex",
          alignItems: "center",
          gap: 12,
          marginLeft: isNarrowViewport ? "4px" : "20px",
        }}
      >
        {username && (
          <p style={{ margin: 0 }}>
            <span className="user-broker">
              <span
                className="user-name"
                onDoubleClick={() => setShowServerConnections(true)}
                style={{ cursor: "pointer", userSelect: "none" }}
                title="Clique duas vezes para ver as conexões com os servidores"
              >
                {isNarrowViewport ? getBroker() : `${getBroker()}:${username}`}
              </span>
            </span>
          </p>
        )}
        <RemoteLogIndicator />
      </div>

      <div
        className="footer-center"
        style={{
          order: 2,
          flex: 1,
          minWidth: 0,
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          gap: 12,
        }}
      >
        {/* Workspace menus */}
        <WorkspaceMenus />
      </div>

      <div
        className="footer-right"
        style={{
          order: 3,
          display: "flex",
          alignItems: "center",
          gap: 16,
          flexShrink: 0,
        }}
      >
        {/* Status indicator — dot only on narrow viewports */}
        <span
          className={`status-label status-background ${online ? "online" : "offline"}`}
          style={{
            whiteSpace: "nowrap",
            display: "flex",
            alignItems: "center",
            gap: 6,
            flexShrink: 0,
          }}
        >
          <span
            style={{
              display: "inline-block",
              width: 12,
              height: 12,
              borderRadius: "50%",
              background: online ? "#28a745" : "#dc2626",
              boxShadow: `0 0 4px ${online ? "#28a745" : "#dc2626"}`,
            }}
          />
          {!isNarrowViewport && (online ? "Dados Online" : "Dados Offline")}
        </span>

        {/* Broker status button (opens Router login modal or disconnect confirmation) */}
        {(() => {
          const isSimulator =
            typeof routerBroker === "string" &&
            routerBroker.toLowerCase() === "simulator";
          const dotColor = !routerOnline
            ? "#dc2626"
            : isSimulator
              ? "#ffffff"
              : "#28a745";
          const dotShadow = `0 0 4px ${dotColor}`;
          const textColor = routerOnline ? "#28a745" : "#dc2626";
          return (
            <button
              type="button"
              className={`status-label status-background ${routerOnline ? "online" : "offline"}`}
              onClick={handleRouterButtonClick}
              style={{
                whiteSpace: "nowrap",
                display: "flex",
                alignItems: "center",
                gap: 8,
                flexShrink: 0,
                border: "none",
                cursor: "pointer",
              }}
              data-evo-id="footer-btn-router-status"
            >
              {/* status dot: green=broker online, white=simulator online, red=offline */}
              <span
                style={{
                  display: "inline-block",
                  width: 12,
                  height: 12,
                  borderRadius: "50%",
                  background: dotColor,
                  boxShadow: dotShadow,
                  border:
                    isSimulator && routerOnline ? "1px solid #999" : "none",
                }}
              />

              {/* broker label + status text (hidden on narrow viewports) */}
              {!isNarrowViewport &&
                (routerBroker ? (
                  isSimulator ? (
                    <>
                      <span className="broker-badge simulator">SIMULADOR</span>
                      <span
                        style={{
                          marginLeft: 2,
                          color: textColor,
                          fontWeight: 600,
                        }}
                      >
                        {routerOnline ? "Online" : "Offline"}
                      </span>
                    </>
                  ) : (
                    <>
                      <span style={{ fontWeight: 600, marginRight: 2 }}>
                        Corretora
                      </span>
                      <span>{routerBroker}</span>
                      <span
                        style={{
                          marginLeft: 2,
                          color: textColor,
                          fontWeight: 600,
                        }}
                      >
                        {routerOnline ? "Online" : "Offline"}
                      </span>
                    </>
                  )
                ) : (
                  <span style={{ color: textColor, fontWeight: 600 }}>
                    {routerOnline ? "Router Online" : "Router Offline"}
                  </span>
                ))}
            </button>
          );
        })()}
        <Suspense fallback={<div></div>}>
          <RouterLoginModal
            visible={isRouterLoginModalVisible}
            onHide={() => setIsRouterLoginModalVisible(false)}
          />
        </Suspense>

        {/* Admin News Message Modal */}
        <Suspense fallback={<div></div>}>
          <AdminNewsMessageModal
            visible={isAdminNewsModalVisible}
            onHide={() => setIsAdminNewsModalVisible(false)}
          />
        </Suspense>

        {/* Server Connections Modal */}
        <Suspense fallback={<div></div>}>
          <ServerConnectionsModal
            visible={showServerConnections}
            onHide={() => setShowServerConnections(false)}
          />
        </Suspense>

        {/* Router disconnect confirmation dialog */}
        <ConfirmDialog
          visible={showDisconnectConfirm}
          onHide={() => setShowDisconnectConfirm(false)}
          message={`Deseja desconectar da corretora ${routerBroker || "atual"}?`}
          header="Confirmar Desconexão"
          icon="pi pi-exclamation-triangle"
          accept={handleDisconnectConfirm}
          reject={handleDisconnectReject}
          acceptLabel="Sim, desconectar"
          rejectLabel="Cancelar"
          acceptClassName="p-button-danger"
        />

        {/* Router registration dialog */}
        <RouterRegistrationDialog
          visible={showRouterRegistration}
          onHide={() => setShowRouterRegistration(false)}
          onSubmit={handleRouterRegistrationSubmit}
        />

        <Divider layout="vertical" style={{ height: 18, margin: "0 8px" }} />

        {/* Help button */}
        <Button
          icon="pi pi-question-circle"
          className="p-button-text p-button-rounded"
          onClick={toggleHelpPanel}
          tooltip="Ajuda e Suporte"
          tooltipOptions={{ position: "top" }}
          style={{
            color: "var(--text-color)",
            border: "none",
            boxShadow: "none",
            background: "transparent",
          }}
          data-evo-id="footer-btn-help"
        />

        {/* Help panel dialog */}
        <Dialog
          visible={showHelpPanel}
          onHide={() => setShowHelpPanel(false)}
          header="Ajuda e Suporte"
          dismissableMask
          className="help-panel-dialog"
          style={{ width: "min(400px, 92vw)" }}
          footer={null}
        >
          <div
            className="help-panel-content"
            style={{ display: "flex", flexDirection: "column", gap: 10 }}
          >
            {/* Admin-only: News Message Broadcaster */}
            {isAdmin && (
              <>
                <button
                  onClick={() => {
                    setIsAdminNewsModalVisible(true);
                    setShowHelpPanel(false);
                  }}
                  className="help-link"
                  style={{
                    textDecoration: "none",
                    color: "var(--orange-500)",
                    padding: "6px 0",
                    borderBottom: "1px solid var(--surface-border)",
                    background: "transparent",
                    border: "none",
                    textAlign: "left",
                    cursor: "pointer",
                    fontWeight: 600,
                    fontSize: "12px",
                  }}
                  data-evo-id="footer-btn-admin-news"
                >
                  <i className="pi pi-megaphone" style={{ marginRight: 8 }}></i>
                  Enviar Mensagem de Notícias (Admin)
                </button>
                <div
                  style={{
                    borderBottom: "2px solid var(--surface-border)",
                    margin: "2px 0",
                  }}
                ></div>
              </>
            )}

            <a
              href="#"
              onClick={(e) => {
                e.preventDefault();
                showRatingModal();
                setShowHelpPanel(false);
              }}
              className="help-link"
              style={{
                textDecoration: "none",
                color: "var(--primary-color)",
                padding: "6px 0",
                fontSize: "12px",
                borderBottom: "1px solid var(--surface-border)",
              }}
              data-evo-id="footer-btn-rate-app"
            >
              <i className="pi pi-star" style={{ marginRight: 8 }}></i>
              Avalie o aplicativo
            </a>
            <a
              href="mailto:suporte@itevolution.com.br"
              className="help-link"
              style={{
                textDecoration: "none",
                color: "var(--primary-color)",
                padding: "6px 0",
                fontSize: "12px",
                borderBottom: "1px solid var(--surface-border)",
              }}
              data-evo-id="footer-link-feedback"
            >
              <i className="pi pi-envelope" style={{ marginRight: 8 }}></i>
              Conte-nos o que achou desta versão!
            </a>
            <a
              href="https://tradezone.com.br/enviar-ticket/"
              target="_blank"
              rel="noopener noreferrer"
              className="help-link"
              style={{
                textDecoration: "none",
                color: "var(--primary-color)",
                padding: "6px 0",
                fontSize: "12px",
                borderBottom: "1px solid var(--surface-border)",
              }}
              data-evo-id="footer-link-support"
            >
              <i className="pi pi-headphones" style={{ marginRight: 8 }}></i>
              Central de Suporte
            </a>
            <a
              href="https://tradezone.com.br/wp-content/videos/nc/xpi/?link=Apresenta"
              target="_blank"
              rel="noopener noreferrer"
              className="help-link"
              style={{
                textDecoration: "none",
                color: "var(--primary-color)",
                padding: "6px 0",
                fontSize: "12px",
                borderBottom: "1px solid var(--surface-border)",
              }}
              data-evo-id="footer-link-youtube"
            >
              <i className="pi pi-play" style={{ marginRight: 8 }}></i>
              Aprenda a usar o novo WebCharts HTML
            </a>
            <a
              href="/whats-new.html"
              target="_blank"
              rel="noopener noreferrer"
              className="help-link"
              style={{
                textDecoration: "none",
                color: "var(--primary-color)",
                padding: "6px 0 0 0",
                fontSize: "12px",
              }}
              data-evo-id="footer-link-whats-new"
            >
              <i className="pi pi-sparkles" style={{ marginRight: 8 }}></i>O que
              há de novo?
            </a>
          </div>
        </Dialog>
      </div>
    </footer>
  );
}

export default Footer;
